import { animate, View } from '@play-co/timestep-core/ui';
import SceneManager from '../logic/SceneManager';
import StateObserver from '../../StateObserver';
import { getScreenTop, waitForItPromise } from '../../lib/utils';
import { startSceneTransition } from '../../state/ui';
import { getScenePosition } from './position.config';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import { Entity } from '../logic/EntityManager';
import { Position } from './PositionModel';

export class PositionController<V extends Entity = Entity> {
  public get position() {
    return this._position;
  }
  protected set position(position: Position | null) {
    this._position = position;
  }
  protected _position: Position;

  constructor(protected sceneManager: SceneManager, protected view: V) {}

  public init(position: Position) {
    if (!position) return;

    this.position = position;
    const sceneView = this.sceneManager.scenes[position?.scene]?.getView();

    const destination = getScenePosition(position);
    this.view.updateOpts({
      superview: sceneView,
      ...destination,
      zIndex: destination.y,
    });
    this.view.style.y -= getScreenTop();
  }

  public async moveTo(position: Position | null, duration?: number) {
    this.view.moving = true;
    this.position = position;
    const sceneView = this.sceneManager.scenes[position?.scene]?.getView();
    if (!sceneView) {
      await animate(this.view)
        .clear()
        .now({ opacity: 0 }, 500)
        .then(() => {
          this.view.removeFromSuperview();
          this.view.style.opacity = 1;
        })
        .toPromise();

      this.view.moving = false;
      return;
    }

    const isSceneTransition = sceneView !== this.view.getSuperview();
    if (isSceneTransition) {
      StateObserver.dispatch(startSceneTransition(position.scene));
    }

    const destination = getScenePosition(position);
    if (duration == null) {
      duration = this.getTransitionDuration(sceneView, destination);
    }

    if (!isSceneTransition) {
      this.view.setFlipped(destination.x > this.view.style.x);
    }
    this.prepareForTransition(sceneView);
    await this.animateViewPosition(sceneView, destination, duration);

    if (isSceneTransition) {
      await waitForItPromise();
    }
    this.view.moving = false;
  }

  public getSceneLocation() {
    if (['livingRoom', 'friendsLivingRoom'].includes(this.position.scene)) {
      return this.position.location;
    }

    return 'c';
  }

  protected prepareForTransition(sceneView: View) {
    this.view.updateOpts({ superview: sceneView });
  }

  protected getTransitionDuration(
    sceneView: View,
    destination: Partial<ViewPosition>,
  ) {
    if (sceneView !== this.view.getSuperview()) {
      return 0;
    }

    const { x, y } = this.view.style;
    const distanceX = Math.abs(x - destination.x);
    const distanceY = Math.abs(y - destination.y);
    const distance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2));
    return 4 * distance;
  }

  protected async animateViewPosition(
    sceneView: View,
    destination: Partial<ViewPosition & { zIndex: number }>,
    duration: number,
  ) {
    await animate(this.view)
      .clear()
      .then(
        {
          ...destination,
          scale: destination.scale ?? 1,
          zIndex: destination.zIndex ?? destination.y,
        },
        duration,
        animate.easeInOutSine,
      )
      .then(() => {
        if (this.view.getSuperview() !== sceneView) {
          this.view.updateOpts({ superview: sceneView });
          this.view.style.y -= getScreenTop();
        }
        this.view.setFlipped(false);
      })
      .toPromise();
  }

  public destroy() {
    void this.moveTo(null);
  }
}
